import React, { useRef, useEffect, useMemo, useState } from "react";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import es from "react-phone-input-2/lang/es.json";
import it from "react-phone-input-2/lang/it.json";
import de from "react-phone-input-2/lang/de.json";
import fr from "react-phone-input-2/lang/fr.json";

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const LabelledMultiline = styled.div`
  color: ${(props) => props?.nero};
  font-size: 15px;
  font-weight: 600;
  word-break: break-all;
`;

const Input = styled.input`
  font-weight: 600 !important;
  height: auto !important;
  padding: 0 !important;
  color: ${(props) => props?.gray};
  border: none;
  border-radius: 0;
  border-bottom: 2px solid
    ${(props) => (props.hasError ? props.theme?.cardinal : props.theme?.gray)} !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:hover,
  &:focus {
    border-bottom: 2px solid
      ${(props) =>
        props.hasError
          ? props.theme?.cardinal
          : props.greenTheme
          ? props.theme?.yellowGreen
          : props.theme?.darkPurple} !important;
  }
`;

const PhoneInputWrapper = styled.div`
  & input {
    font-weight: 600 !important;
    left: 37px;
    width: calc(100% - 37px) !important;
    font-size: 1rem !important;
    border-top: 0px solid !important;
    border-right: 0px solid !important;
    border-radius: 0px !important;
    background: transparent !important;
    height: auto !important;
    padding: 0 !important;
    border-bottom: 2px solid
      ${(props) =>
        props.hasError
          ? props.theme?.cardinal
          : props.greenTheme
          ? props.theme?.yellowGreen
          : props.theme?.gray} !important;

    &:hover,
    &:focus {
      border-bottom: 2px solid
        ${(props) =>
          props.hasError
            ? props.theme?.cardinal
            : props.greenTheme
            ? props.theme?.yellowGreen
            : props.theme?.darkPurple} !important;
    }

    &[readonly],
    &[disabled] {
      color: ${(props) => props.theme?.nero} !important;
      cursor: default;
      border-bottom: 0px !important;
      background: transparent !important;
    }
  }

  & .flag-dropdown {
    border: 0px !important;
  }

  &:focus {
    box-shadow: 0 0.2rem 1rem transparent;
  }
`;

const Error = styled.span`
  color: ${(props) => props.theme?.cardinal};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const LabelledInput = (props) => {
  const {
    className,
    title,
    value,
    onChange,
    onCopy,
    onDrag,
    onDrop,
    onPaste,
    onBlur,
    onInput,
    type,
    country,
    hasError,
    errorMessage,
    pattern,
    placeholder,
    readOnly,
    disabled,
    autoFocus,
    greenTheme,
    locale,
    tabIndex,
  } = props;

  const inputRef = useRef();

  /*
  Need this to avoid phone number being cleanned - lib bug
  */

  const [phoneProp, setPhoneProp] = useState(value);

  useEffect(() => {
    setPhoneProp(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const isReadOnlyElement = readOnly ? { readOnly: true, disabled: true } : "";

  const localization = useMemo(() => {
    if (locale === "es" || locale === "es-US") {
      return es;
    }

    if (locale === "it") {
      return it;
    }

    if (locale === "de") {
      return de;
    }

    if (locale === "fr" || locale === "fr-CA") {
      return fr;
    }

    return null;
  }, [locale]);

  return (
    <div className={className}>
      <Label>{title}</Label>
      {type === "tel" ? (
        <PhoneInputWrapper data-testid={props["data-testid"]}>
          <PhoneInput
            key={locale}
            country={country}
            hasError={hasError}
            {...(localization && { ...{ localization } })}
            type={type}
            countryCodeEditable={false}
            className="form-control phone-input"
            value={phoneProp}
            placeholder={placeholder}
            onChange={onChange}
            pattern={pattern}
            {...isReadOnlyElement}
            greenTheme={greenTheme}
          />
        </PhoneInputWrapper>
      ) : readOnly ? (
        <LabelledMultiline data-testid={props["data-testid"]}>
          {value}
        </LabelledMultiline>
      ) : (
        <Input
          tabIndex={tabIndex}
          ref={inputRef}
          hasError={hasError}
          data-testid={props["data-testid"]}
          type={type}
          className="form-control"
          value={value || ""}
          placeholder={placeholder}
          onChange={onChange}
          onCopy={onCopy}
          onDrag={onDrag}
          onDrop={onDrop}
          onPaste={onPaste}
          onInput={onInput}
          pattern={pattern}
          onBlur={onBlur}
          disabled={disabled}
          greenTheme={greenTheme}
        />
      )}
      {hasError && errorMessage && (
        <Error data-testid="error-message">{errorMessage}</Error>
      )}
    </div>
  );
};

LabelledInput.defaultProps = {
  className: "",
  type: "text",
  hasError: false,
  errorMessage: null,
  pattern: null,
  onChange: null,
  onInput: null,
  onBlur: null,
  placeholder: "",
  disabled: false,
  autoFocus: false,
  tabIndex: null,
};

LabelledInput.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onInput: PropTypes.func,
  type: PropTypes.oneOf(["text", "number", "tel", "email", "password"]),
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  tabIndex: PropTypes.number,
};

export default withTheme(LabelledInput);
